import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Page404 } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import {
  calculateEnhancedTeamStructure,
  ProjectData,
  TeamShowType,
  TeamType,
  ProjectSecurityContext,
} from 'domain/shared/newProject';
import { useStaffingProjectData } from '../hooks/useStaffingProjectData';
import { Loading } from 'framework/ui/mui-builder';
import {
  Permission,
  PermissionsMap,
  SecurityContextTemplate,
  useGlobalSecurityReactContext,
} from 'framework/authorization';
import { StaffingDataType } from 'domain/shared/staffing';

interface StaffingProjectContextData {
  data: TeamShowType;
  project: ProjectData;
  staffingData: StaffingDataType | null;
  refetch: () => Promise<void>;
}

const StaffingProjectDataContext = createContext<Nullable<StaffingProjectContextData>>(null);
const STAFFING_PROJECT_CONTEXT = 'STAFFING_PROJECT_CONTEXT';

export const useStaffingProjectContext = () => {
  const context = useContext(StaffingProjectDataContext);
  if (!context) {
    throw new Error('useStaffingProjectContext must be used within StaffingProjectContext');
  }
  return context;
};

function buildMergedTeamData(teamData: TeamType | null | undefined, project: ProjectData): TeamShowType {
  const projectBudget = (project as ProjectData & { budget?: { fte?: string; startDate?: string; endDate?: string } })
    .budget;
  const enhanced = calculateEnhancedTeamStructure(teamData?.team, projectBudget?.fte, project.budget?.billingType);
  const baseBudget = teamData?.budget ?? { fte: projectBudget?.fte ?? '' };
  const budget =
    projectBudget?.startDate && projectBudget?.endDate
      ? { ...baseBudget, startDate: projectBudget.startDate, endDate: projectBudget.endDate }
      : baseBudget;
  return {
    ...(teamData || {}),
    ...enhanced,
    id: teamData?.id ?? project.id.toString(),
    criticalPeriods: teamData?.criticalPeriods ?? [],
    upcomingChanges: teamData?.upcomingChanges ?? [],
    budget,
    team: {
      ...(teamData?.team || {}),
      ...enhanced.team,
      id: enhanced.team?.id ?? teamData?.team?.id ?? '',
      members: teamData?.team?.members ?? enhanced.team?.members ?? [],
      expectedStructure: enhanced.team?.expectedStructure ?? teamData?.team?.expectedStructure ?? [],
      updatedAt: teamData?.team?.updatedAt ?? enhanced.team?.updatedAt ?? null,
    },
  };
}

export const StaffingProjectContext = ({
  children,
  permissionToCheck,
}: PropsWithChildren<{ permissionToCheck?: Permission | Permission[] }>) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isAuthenticated } = useAuthenticationService();
  const { project, teamData, rawItem, refetch, isDataReloading, error } = useStaffingProjectData(
    projectId ?? undefined,
  );
  const globalContext = useGlobalSecurityReactContext() || {};

  const { contextValue, projectPermissionsInGlobalFormat, projectPermissions } = useMemo(() => {
    if (!project) {
      return {
        contextValue: null,
        projectPermissionsInGlobalFormat: [],
        projectPermissions: undefined,
      };
    }

    const merged = buildMergedTeamData(teamData, project);
    const staffingData = rawItem as StaffingDataType | null;
    const permissionsGlobalFormat =
      project?.accessInfo?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [];

    return {
      contextValue: {
        data: merged,
        project,
        staffingData,
        refetch: async () => {
          await refetch();
        },
      } as StaffingProjectContextData,
      projectPermissionsInGlobalFormat: permissionsGlobalFormat,
      projectPermissions: project.accessInfo,
    };
  }, [project, teamData, rawItem, refetch]);

  if (isDataReloading) {
    return <Loading isInitialPosition />;
  }

  if (!contextValue) {
    return error ? null : <Page404 isAuthenticated={isAuthenticated} />;
  }

  return (
    <SecurityContextTemplate
      id={projectId}
      getPermissionsFunc={() =>
        Promise.all([
          Promise.resolve(Object.keys(globalContext.permissions as unknown as PermissionsMap) || []),
          Promise.resolve(projectPermissionsInGlobalFormat || []),
        ]).then((permissions: string[][]) =>
          permissions.reduce((flatten: string[], arr: string[]) => [...flatten, ...arr], []),
        )
      }
      permissionToCheck={permissionToCheck}
      contextName={STAFFING_PROJECT_CONTEXT}
    >
      <ProjectSecurityContext permissions={projectPermissions}>
        <StaffingProjectDataContext.Provider value={contextValue}>{children}</StaffingProjectDataContext.Provider>
      </ProjectSecurityContext>
    </SecurityContextTemplate>
  );
};
